import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { sortNumbers, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user.js';

import { BLANK_FILTER_TEXT } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions.js';
import { RULE_PRIORITY, RULE_PRIORITY_INDEX } from '../../../constants/rule';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations.js';

import { fetchRules } from '../../../redux/actions/rule';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setSortedData } from '../../../redux/reducers/rule';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import Priority from '../../../components/PriorityTag';
import RuleSnoozeIcon from '../../../components/RuleSnoozeIcon';
import { errorToastHandler } from '../../../components/action_notifier';

import { useFilterOptions } from './useFilterOptions';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

export default function Rules() {
  const dispatch = useDispatch();

  const { data: rules, loading, sortedData } = useSelector(state => state.rule);
  const { permissions } = useSelector(state => state.user);
  const { selectedFilters } = useSelector(state => state.filters);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [state, setState] = useState({
    alert_modal: false,
    delete_modal: false,
    modal_message: '',
    modal_title: '',
    modal_type: '',
    selected_rules: []
  });

  useEffect(() => {
    getRules();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (rules?.length) {
      dispatch(setSortedData(rules));
    }
  }, [rules]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && rules?.length) {
      dispatch(setSortedData(rules));
    }
  }, [filteredInfo]);

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilteredInfo(filters);

      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, dispatch]
  );

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const selectRecords = selected => {
    setState(prevState => ({ ...prevState, selected_rules: selected }));
  };

  async function getRules(query) {
    try {
      const response = await dispatch(fetchRules({ query })).unwrap();

      if (response) {
        setState(prevState => ({
          ...prevState,
          rules: response
        }));
      }
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function sigleAction(id, type) {
    const modal_type = type === 'snoozable' ? 'Snooze' : 'Unsnooze';
    const selected_rules = [];

    selected_rules.push(id);
    setState(prevState => ({ ...prevState, selected_rules, modal_type: modal_type }));
    toggleSingleModal('delete_modal', modal_type);
  }

  const toggleSingleModal = (modal, type) => {
    const title = type === 'Snooze' ? 'Snooze rule' : 'Unsnooze rule';
    const message =
      type === 'Snooze' ? 'Are you sure you want to snooze this rule?' : 'Are you sure you want to unsnooze this rule?';

    setState(prevState => ({
      ...prevState,
      [modal]: !prevState[modal],
      modal_type: type,
      modal_title: title,
      modal_message: message
    }));
  };

  async function deleteRules() {
    let response = {};

    if (state.modal_type === 'Archive') {
      response = await axios.delete('rules/bulk', {
        data: { ids: state.selected_rules, force: true }
      });
    } else if (state.modal_type === 'Snooze') {
      response = await axios.put('rules/snooze/bulk', {
        ids: state.selected_rules,
        force: true,
        seconds: 86400
      });
    } else {
      response = await axios.put('rules/snooze/bulk', {
        ids: state.selected_rules,
        force: true,
        seconds: -1
      });
    }

    if (response.status && response.status === 200) {
      await getRules();
      setState(prevState => ({ ...prevState, delete_modal: false, selected_rules: [] }));
    }
  }

  const archiveRule = () => {
    let isDefaultRuleExists = false;

    state.selected_rules?.map(x => {
      const rule = rules?.filter(data => data.id === x);

      if (rule[0] && rule[0].default_rule === 1) {
        isDefaultRuleExists = true;
      }
    });

    isDefaultRuleExists ? toggleModal('alert_modal', 'Alert') : toggleModal('delete_modal', 'Archive');
  };

  const toggleModal = (modal, type) => {
    const title =
      type === 'Archive'
        ? 'Archive rules'
        : type === 'Snooze'
          ? 'Snooze rules'
          : type === 'Alert'
            ? 'Alert'
            : 'Unsnooze rules';
    const message =
      type === 'Archive'
        ? 'Are you sure you want to archive selected rules? This action cannot be undone.'
        : type === 'Snooze'
          ? 'Are you sure you want to snooze selected rules?'
          : type === 'Alert'
            ? 'This is a default system Rule which can not be edited or archived.'
            : 'Are you sure you want to unsnooze selected rules?';

    setState(prevState => ({
      ...prevState,
      [modal]: !prevState[modal],
      modal_type: type,
      modal_title: title,
      modal_message: message
    }));
  };

  function filtersUpdated(filters) {
    setState(prevState => ({ ...prevState, filters: { ...prevState.filters, ...filters } }));
  }

  const filtersClose = () => {
    setState(prevState => ({ ...prevState, filters_open: false }));
  };

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.RULES.CREATE], permissions)) {
      availableActions.push({
        label: (
          <Link className="app-link" to="/rules/builder/new">
            Add rule
          </Link>
        )
      });
    }

    if (hasPermission([PERMISSIONS.RULES.UPDATE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selected_rules?.length}
            onClick={() => toggleModal('delete_modal', 'Snooze')}>
            Snooze rule
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.RULES.UPDATE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selected_rules?.length}
            onClick={() => toggleModal('delete_modal', 'Unsnooze')}>
            Unsnooze rule
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.RULES.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selected_rules?.length}
            onClick={archiveRule}>
            Archive rule
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [selectedFilters, state.selected_rules, permissions]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Rule ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.XS,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier),
      render: (value, record) => (
        <Link to={`rule/${record.id}`}>
          {record.is_snoozed && <RuleSnoozeIcon />} {record.identifier}
        </Link>
      )
    },
    {
      ...commonColumnProperties,
      title: 'Rule Name',
      dataIndex: 'name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      filteredValue: filteredInfo.name || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.name, b.name)
    },
    {
      ...commonColumnProperties,
      title: 'Rule Type',
      dataIndex: 'type_display_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
      filteredValue: filteredInfo.type_display_name || null,
      filters: columnFilterOptions?.types,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.type_display_name : record?.type_display_name === value,
      sorter: (a, b) => sortStrings(a.type_display_name, b.type_display_name)
    },
    {
      ...commonColumnProperties,
      title: 'Rule Priority',
      dataIndex: 'rulepid',
      configColumnKey: TABLE_CONFIG_COLUMNS.PRIORITY,
      filteredValue: filteredInfo.rulepid || null,
      filters: columnFilterOptions?.priorities,
      width: COLUMN_SIZE.MD,
      render: (value, record) => {
        const priority = record.data.action ? RULE_PRIORITY[record.data.action.value] : '';
        return <Priority priority={priority} />;
      },
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.data.action.value : record?.data.action.value === value,
      sorter: (a, b) => {
        const strA = a.data.action ? RULE_PRIORITY_INDEX[a.data.action.value] : -1;
        const strB = b.data.action ? RULE_PRIORITY_INDEX[b.data.action.value] : -1;

        return sortNumbers(strA, strB);
      }
    },
    {
      ...commonColumnProperties,
      title: 'Rule Status',
      dataIndex: 'rulestatusid',
      configColumnKey: TABLE_CONFIG_COLUMNS.STATUS,
      filteredValue: filteredInfo.rulestatusid || null,
      filters: columnFilterOptions?.statuses,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.snooze_status : record?.snooze_status === value,
      render: (value, record) => (record.is_snoozed ? 'Snoozed' : 'Active'),
      sorter: (a, b) => {
        const strA = a.is_snoozed ? 'Snoozed' : 'Active';
        const strB = b.is_snoozed ? 'Snoozed' : 'Active';

        return sortStrings(strA, strB);
      }
    },

    {
      ...commonColumnProperties,
      title: 'Rule Triggers',
      dataIndex: 'triggers_count',
      configColumnKey: TABLE_CONFIG_COLUMNS.TRIGGERS,
      filteredValue: filteredInfo.triggers_count || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => record.triggers_count === +value,
      sorter: (a, b) => sortNumbers(a.triggers_count, b.triggers_count)
    },
    {
      ...commonColumnProperties,
      title: 'Rule Objects',
      dataIndex: 'objects_count',
      configColumnKey: TABLE_CONFIG_COLUMNS.OBJECTS,
      filteredValue: filteredInfo.objects_count || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => record.objects_count === +value,
      sorter: (a, b) => sortNumbers(a.objects_count, b.objects_count)
    },
    {
      title: 'Snooze',
      dataIndex: 'actions',
      configColumnKey: TABLE_CONFIG_COLUMNS.SNOOZE,
      align: 'center',
      ellipsis: true,
      filteredValue: filteredInfo.rulesnoozzeid || null,
      fixed: 'right',
      noPadding: true,
      width: COLUMN_SIZE.XS,
      accessor: d => ({ snooze: d.is_snoozed ? 'snoozed' : 'snoozable', id: d.id }),
      render: (value, record) => {
        const snooze = record.is_snoozed ? 'snoozed' : 'snoozable';
        return (
          <>
            <i
              style={{ cursor: 'pointer' }}
              title={record.is_snoozed ? 'Unsnooze' : 'Snooze'}
              onClick={() => sigleAction(record.id, snooze)}
              className={'rule_not-action mdi mdi-alarm ' + snooze}></i>
          </>
        );
      }
    }
  ];

  return (
    <>
      <Card>
        <AppTable
          description="List of Rules"
          headerClass="py-2"
          searchPlaceholder="Search Rules"
          title="Rules"
          actions={actions}
          baseColumns={columns}
          dataSource={rules}
          filterable={false}
          loading={loading}
          tableConfigKey={TABLE_CONFIG_KEYS.RULES}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getRules}
        />
      </Card>

      <AppModal
        confirmButtonColor="danger"
        confirmButtonText={state.modal_type}
        isOpen={state.delete_modal}
        title={state.modal_title}
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={deleteRules}>
        <div className="py-4">{state.modal_message}</div>
      </AppModal>

      <AppModal
        confirmButtonText="Close"
        cancelButtonHidden={true}
        isOpen={state.alert_modal}
        title={state.modal_title}
        handleCancel={() => toggleModal('alert_modal')}
        handleConfirm={() => toggleModal('alert_modal')}>
        <div className="py-4">{state.modal_message}</div>
      </AppModal>
    </>
  );
}
